import * as Yup from 'yup';
import {
    isValidPhone,
    isValidCNPJ,
    isValidCPF
} from '@brazilian-utils/brazilian-utils';

import { CONFIG } from 'constants/constants';
import { LoanApplication, Borrower } from 'generated/graphql';
import { Reason, BorrowerRelationship } from 'enums/enums';
import { formatMoneyWithoutDecimals, validateEmail } from 'utils/utils';

Yup.setLocale({
    mixed: {
        required: 'Este campo é obrigatório',
    },
    string: {
        min: 'Este campo é obrigatório',
        email: 'Escreva um e-mail válido'
    }
});

export const amountRequestedSchema = (loanAmountMin: number, loanAmountMax: number) =>
    Yup.object().shape<LoanApplication>({
    amountRequested: Yup.number().test(
        '',
        '',
        (amountRequested: number) =>
            amountRequested >= loanAmountMin &&
            amountRequested <= loanAmountMax
    ),
});

export const reasonSchema = Yup.object().shape<LoanApplication>({
    reason: Yup.mixed().test(
        '',
        'Este campo é obrigatório',
        (reason: string) => Reason[reason]
    ),
    reasonDetail: Yup.string().nullable(),
});

export const aboutCompanySchema = Yup.object().shape<Borrower>({
    companyDocument: Yup.string().test(
        '',
        'O número de CNPJ digitado não existe',
        isValidCNPJ
    ),
    monthlyRevenue: Yup.mixed()
        .test(
            '',
            'Este campo é obrigatório',
            (monthlyRevenue: string) => Number(monthlyRevenue) > 0
        )
        .test(
            '',
            `O valor não pode ultrapassar R$${formatMoneyWithoutDecimals(
                CONFIG.MONTHLY_REVENUE.MAX()
            )} por mês`,
            (monthlyRevenue: number) =>
                monthlyRevenue <= CONFIG.MONTHLY_REVENUE.MAX()
        ),
    relationship: Yup.mixed().test(
        '',
        'Este campo é obrigatório',
        (relationship: string) => BorrowerRelationship[relationship]
    ),
});

export const passwordSchema = Yup.object().shape({
    password: Yup.string()
        .min(8, '')
        .required('Este campo é obrigatório'),
    passwordConfirmation: Yup.string()
        .oneOf([Yup.ref('password')], 'As senhas digitadas não conferem')
        .required('Este campo é obrigatório'),
});

export const resposibleInfoSchema = Yup.object().shape<LoanApplication>({
    borrower: Yup.object().shape<Borrower>({
        responsibleName: Yup.string().test(
            '',
            'Nome incompleto',
            value => value && value.trim().split(' ').length > 1
        ),
        email: Yup.string()
            .required('Este campo é obrigatório')
            .test('', 'Escreva um e-mail válido', validateEmail),
        responsibleDocument: Yup.string().test('', 'CPF inválido', isValidCPF),
        phone: Yup.string().test('', 'Celular inválido', isValidPhone),
    }),
});

export const privacyTermsSchema = Yup.object().shape({
    authorizeInformationAccess: Yup.bool().oneOf(
        [true],
        'Este campo é obrigatório'
    ),
    agreeTerms: Yup.bool().oneOf([true], 'Este campo é obrigatório'),
    notifyViaWhatsapp: Yup.bool().nullable(),
    email: Yup.string()
        .required('Este campo é obrigatório')
        .test('', 'Escreva um e-mail válido', validateEmail),
});

export const privacyTermsToPartnersSchema = Yup.object().shape({
    authorizeInformationAccess: Yup.bool().oneOf(
        [true],
        'Este campo é obrigatório'
    ),
    agreeTerms: Yup.bool().oneOf([true], 'Este campo é obrigatório'),
    partnerTerms: Yup.bool().oneOf([true], 'Este campo é obrigatório'),
    notifyViaWhatsapp: Yup.bool().nullable(),
    email: Yup.string()
        .required('Este campo é obrigatório')
        .test('', 'Escreva um e-mail válido', validateEmail),
});

export const preApprovedHomeSchema = Yup.object().shape({
    companyDocument: Yup.string().test(
        '',
        'O número de CNPJ digitado não existe',
        isValidCNPJ
    ),
});

export const companyInfoSchema = Yup.object().shape<any>({
    borrower: Yup.object().shape<Borrower>({
        responsibleName: Yup.string().test(
            '',
            'Nome incompleto',
            value => value && value.trim().split(' ').length > 1
        ),
        email: Yup.string()
            .required('Este campo é obrigatório')
            .test('', 'Escreva um e-mail válido', validateEmail),
        responsibleDocument: Yup.string().test('', 'CPF inválido', isValidCPF),
        phone: Yup.string().test('', 'Celular inválido', isValidPhone),
        relationship: Yup.mixed().test(
            '',
            'Este campo é obrigatório',
            (relationship: string) => BorrowerRelationship[relationship]
        ),
        monthlyRevenue: Yup.mixed()
        .test(
            '',
            'Este campo é obrigatório',
            (monthlyRevenue: string) => Number(monthlyRevenue) > 0
        )
        .test(
            '',
            `O valor não pode ultrapassar R$${formatMoneyWithoutDecimals(
                CONFIG.MONTHLY_REVENUE.MAX()
            )} por mês`,
            (monthlyRevenue: number) =>
                monthlyRevenue <= CONFIG.MONTHLY_REVENUE.MAX()
        )
        .test(
            '',
            `O valor não pode ser menor que R$${formatMoneyWithoutDecimals(
                CONFIG.MONTHLY_REVENUE.MIN()
            )} por mês`,
            (monthlyRevenue: number) =>
                monthlyRevenue >= CONFIG.MONTHLY_REVENUE.MIN()
        ),
    }),
    authorizeInformationAccess: Yup.bool().oneOf(
        [true],
        'Este campo é obrigatório'
    ),
    agreeTerms: Yup.bool().oneOf([true], 'Este campo é obrigatório'),
});
